import { config } from "../../../config";
import { countMessageTokens, queryClaudeWithContext } from "../../../services/claude";
import { REQUEST_CANCELED_ERROR } from "../constants/errors";
import {
  BinaryNode,
  ChunkData,
  SynthesisOptions
} from "./chunkSynthesize.types";
import { getExtractedDataForSynthesis } from "./extractedDataHandler";

const SYNTHESIS_MESSAGES = {
  SPLITTING: "Splitting data into smaller chunks for processing...",
  MERGING: "Merging synthesis results",
  LEVEL: "Level",
  PROCESSING: "Processing",
  CHUNKS: "chunks"
} as const;

const EMPTY_COUNT = 0;
const SINGLE_CHUNK = 1;
const BINARY_SPLIT_DIVISOR = 2;
const INDEX_OFFSET = 1;
const LEVEL_INCREMENT = 1;
const ROOT_LEVEL = 0;
const DEFAULT_MAX_TOKENS = 8192;
const FIRST_CHUNK_INDEX = 0;

const throwIfAborted = (signal?: AbortSignal): void => {
  if (signal?.aborted) {
    throw new Error(REQUEST_CANCELED_ERROR);
  }
};

const checkIfWithinLimit = async (chunks: ChunkData[], options: SynthesisOptions): Promise<boolean> => {
  throwIfAborted(options.abortSignal);

  const tokenCheck = await countMessageTokens({
    context: chunks,
    query: options.originalQuery,
    systemPrompt: options.systemPrompt,
    model: options.model
  });

  if (tokenCheck.error || !tokenCheck.tokenCount) {
    return false;
  }

  return tokenCheck.tokenCount <= config.maxInputTokens;
};

const rethrowSynthesisError = (error: unknown): never => {
  const errorToThrow = error instanceof Error && error.message === REQUEST_CANCELED_ERROR
    ? new Error(REQUEST_CANCELED_ERROR)
    : error;
  throw errorToThrow;
};

const synthesizeChunks = async (chunks: ChunkData[], options: SynthesisOptions): Promise<string | null> => {
  if (chunks.length === EMPTY_COUNT) {
    return "No data available for synthesis.";
  }

  throwIfAborted(options.abortSignal);

  const result = await queryClaudeWithContext({
    query: options.originalQuery,
    context: chunks,
    messages: options.messageHistory ?? [],
    systemPrompt: options.systemPrompt,
    temperature: options.temperature,
    maxTokens: options.maxTokens ?? DEFAULT_MAX_TOKENS,
    budgetTokens: options.budgetTokens,
    effort: options.effort,
    model: options.model,
    thinkingMode: options.thinkingMode,
    onMessageDelta: options.onStreamUpdate,
    onThinkingDelta: options.onThinkingUpdate ?? ((): void => {})
  });

  if (!result.ok) {
    return rethrowSynthesisError(result.error);
  }

  return result.response ?? null;
};

interface SplitChildrenParams {
  leftChunks: ChunkData[];
  rightChunks: ChunkData[];
  leftWithinLimit: boolean;
  rightWithinLimit: boolean;
  level: number;
  options: SynthesisOptions;
}

const createChildNodes = async (params: SplitChildrenParams): Promise<[BinaryNode, BinaryNode]> => {
  const { leftChunks, rightChunks, leftWithinLimit, rightWithinLimit, level, options } = params;
  const nextLevel = level + LEVEL_INCREMENT;

  if (leftWithinLimit && rightWithinLimit) {
    return [
      { data: leftChunks, level: nextLevel },
      { data: rightChunks, level: nextLevel }
    ];
  }

  return Promise.all([
    buildBinaryTree(leftChunks, options, nextLevel),
    buildBinaryTree(rightChunks, options, nextLevel)
  ]);
};

const buildBinaryTree = async (
  chunks: ChunkData[],
  options: SynthesisOptions,
  level: number = ROOT_LEVEL
): Promise<BinaryNode> => {
  const node: BinaryNode = { data: chunks, level };

  if (options.abortSignal?.aborted) {
    console.log("[SYNTHESIS] Operation cancelled during tree building");
    return node;
  }

  if (chunks.length <= SINGLE_CHUNK) {
    console.log(`[SYNTHESIS] Level ${level}: Cannot split ${chunks.length} chunks further`);
    return node;
  }

  console.log(`[SYNTHESIS] Level ${level}: Splitting ${chunks.length} chunks...`);

  const midPoint = Math.ceil(chunks.length / BINARY_SPLIT_DIVISOR);
  const leftChunks = chunks.slice(FIRST_CHUNK_INDEX, midPoint);
  const rightChunks = chunks.slice(midPoint);

  const [leftWithinLimit, rightWithinLimit] = await Promise.all([
    checkIfWithinLimit(leftChunks, options),
    checkIfWithinLimit(rightChunks, options)
  ]);

  console.log("[SYNTHESIS] node limits", { leftWithinLimit, rightWithinLimit, level });

  if (level === ROOT_LEVEL && (!leftWithinLimit || !rightWithinLimit)) {
    options.onStreamUpdate(`\n--- ${SYNTHESIS_MESSAGES.SPLITTING} ---\n`);
  }

  const [leftNode, rightNode] = await createChildNodes({
    leftChunks,
    rightChunks,
    leftWithinLimit,
    rightWithinLimit,
    level,
    options
  });

  node.left = leftNode;
  node.right = rightNode;

  return node;
};

const synthesizeChildNodes = async (node: BinaryNode, options: SynthesisOptions): Promise<(string | null)[]> => {
  const childNodes = [node.left, node.right].filter((child): child is BinaryNode => child !== undefined);
  const results = await Promise.all(childNodes.map((child) => synthesizeNode(child, options)));
  return results.filter((result) => result !== null);
};

const synthesizeNode = async (node: BinaryNode, options: SynthesisOptions): Promise<string | null> => {
  if (options.abortSignal?.aborted) {
    console.log("[SYNTHESIS] Operation cancelled");
    return null;
  }

  if (node.synthesized) {
    console.log(`[SYNTHESIS] Level ${node.level}: Already synthesized, returning cached result`);
    return node.synthesized;
  }

  if (!node.left && !node.right) {
    console.log(`[SYNTHESIS] Level ${node.level}: Leaf node with ${node.data.length} chunks`);
    node.synthesized = await synthesizeChunks(node.data, options);
    return node.synthesized;
  }

  console.log(`[SYNTHESIS] Level ${node.level}: Internal node, processing children first`);
  const childResults = await synthesizeChildNodes(node, options);

  if (options.abortSignal?.aborted) {
    console.log("[SYNTHESIS] Operation cancelled after processing children");
    return null;
  }

  const resultsToMerge = childResults.map((content, index) => ({
    index: index + INDEX_OFFSET,
    content: content as string
  }));

  if (resultsToMerge.length === EMPTY_COUNT) {
    console.log(`[SYNTHESIS] Level ${node.level}: No results to merge`);
    return null;
  }

  console.log(`[SYNTHESIS] Level ${node.level}: Merging ${resultsToMerge.length} results`);
  options.onStreamUpdate(`\n--- ${SYNTHESIS_MESSAGES.LEVEL} ${node.level}: ${SYNTHESIS_MESSAGES.MERGING} ---\n`);

  node.synthesized = await synthesizeChunks(resultsToMerge, options);
  return node.synthesized;
};

const extractSingleChunkContent = (content: unknown): string =>
  typeof content === "string" ? content : JSON.stringify(content);

export const binaryTreeSynthesize = async (options: SynthesisOptions): Promise<string | null> => {
  const savedExtractedData = getExtractedDataForSynthesis();

  const chunks: ChunkData[] = savedExtractedData.map((data, index) => ({
    index: index + INDEX_OFFSET,
    content: data
  }));

  console.log(`[SYNTHESIS] Starting binary tree synthesis of ${chunks.length} extracted data chunks`);

  if (chunks.length === EMPTY_COUNT) {
    return "No extracted data available for synthesis.";
  }

  if (chunks.length === SINGLE_CHUNK) {
    return extractSingleChunkContent(chunks[FIRST_CHUNK_INDEX].content);
  }

  const withinLimit = await checkIfWithinLimit(chunks, options);

  if (withinLimit) {
    console.log(`[SYNTHESIS] All ${chunks.length} chunks within limit, direct synthesis`);
    return synthesizeChunks(chunks, options);
  }

  console.log(`[SYNTHESIS] Building binary tree for ${chunks.length} chunks`);
  options.onStreamUpdate(`\n--- Building processing tree for ${chunks.length} chunks ---\n`);

  const tree = await buildBinaryTree(chunks, options);

  if (options.abortSignal?.aborted) {
    console.log("[SYNTHESIS] Operation cancelled");
    return null;
  }

  console.log("[SYNTHESIS] Starting bottom-up synthesis");
  return synthesizeNode(tree, options);
};

export type { ChunkData, SynthesisOptions } from "./chunkSynthesize.types";
